import { CreditCardGatewayService } from './../../../revenue/shared/credit-card-gateway/credit-card-gateway.service';
import { ArbCommandResultDto } from './../../../revenue/shared/billing/dto/arb-command-result.dto';
import { ArbReadDto } from './../../../revenue/shared/billing/dto/arb-read.dto';
import { NotificationService } from './../../../shared/notification/notification.service';
import { ArbCreateResultDto, isArbId } from './../../../revenue/shared/billing/dto/arb-create-result.dto';
import { CreditCardInputComponent } from './../../../revenue/credit-card-input/credit-card-input.component';
import { ArbService } from './../../../revenue/shared/billing/arb.service';
import { ArbCreateDto, initializedArbCreateDto } from './../../../revenue/shared/billing/dto/arb-create.dto';
import { PaymentTypeService } from './../../../revenue/shared/payment-types/payment-type.service';
import { SettingService } from './../../../config/shared/setting/setting.service';
import { UtilService } from 'src/app/shared';
import { Component, OnInit, AfterViewInit, Output, EventEmitter, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import {
  ObjectValueAccessor,
  CreateObjectValueAccessorProvider,
  CreateObjectValueValidationProvider,
} from '../../../shared/widgets/value-accessors/object-value-accessor';
import { Billing } from '../../../revenue/shared/billing/billing.model';
import { TranslationService } from '../../../shared';
import { BillingCycle } from '../../../revenue/shared/billing/billing-cycle.model';
import { BillingService } from '../../../revenue/shared/billing/billing.service';
import { ArcService, ArcSearch, ArcFormula } from '../../../revenue/shared/arc';
import { RevenueItemService } from '../../../revenue/shared/revenue-items/revenue-item.service';
import { RevenueItem } from '../../../revenue/shared/revenue-items/revenue-item.model';
import { ProfileService } from '../../shared/profile.service';
import { PaymentTypeDto } from 'src/app/revenue/shared/payment-types/dto/payment-type.dto';
import { CreditCardPaymentInfo } from 'src/app/revenue/shared/credit-card/credit-card-payment-info.model';
import { CreditCardGateway } from 'src/app/revenue/shared/credit-card-gateway/credit-card-gateway.model';
import { ProfileRelationshipManagementDto } from '../../shared/dto/profile-relationship-management.dto';
import { ProfileRelationSearch } from '../../shared/profile-relation-search.model';
import { UserRead } from 'src/app/users/shared/user-read.model';
import { UserSearchDto } from 'src/app/users/shared/dto/user-search.dto';
import { UserService } from 'src/app/users/shared/user.service';
import { ACHInfo } from 'src/app/revenue/shared/billing/dto/ach-info.dto';
import { SelectItem } from 'primeng/api';
import { ProfileAddress } from '../../shared/profileAddress.model';
import { ProfileMin } from '../../shared/profile-min.model';
import { PrecznService } from 'src/app/integrations/preczn/shared/preczn.service';
import { TokenizeGatewayDto } from 'src/app/revenue/shared/gateway-resolution/dto/tokenize-gateway.dto';
import { AchInputComponent } from 'src/app/revenue/ach-input/ach-input.component';
import { GatewayResolutionService } from 'src/app/revenue/shared/gateway-resolution/gateway-resolution.service';

@Component({
  selector: 'wl-profile-edit-billing',
  templateUrl: 'profile-edit-billing.component.html',
  styleUrls: ['profile-edit-billing.component.less'],
  providers: [
    CreateObjectValueAccessorProvider(ProfileEditBillingComponent),
    CreateObjectValueValidationProvider(ProfileEditBillingComponent),
    ArcService,
    BillingService,
  ],
    standalone: false
})
export class ProfileEditBillingComponent extends ObjectValueAccessor<Billing> implements OnInit, AfterViewInit {
  @ViewChild(CreditCardInputComponent) ccPaymentComponent: CreditCardInputComponent;
  // The ACH counterpart of ccPaymentComponent: processARBChanges() must reach the widget directly
  // to tokenize, because (achInfoChanges) only tells this host about field edits the staff member
  // made - never about the token, which is minted at submit time.
  @ViewChild(AchInputComponent) achPaymentComponent: AchInputComponent;
  public months: Array<string>;
  public billingCycles: Array<BillingCycle>;
  public anchorDays: Array<number>;
  public arcFormulas: Array<ArcFormula>;
  public revenueItems: Array<RevenueItem>;
  public showBaseField: boolean;
  public showFlatFeeField: boolean;
  public itemHasArc = false;
  public arcId = 0;
  public arcCalculation = 0;
  public useArc = false;
  public arbIsEnabled = false;
  public arbSelected = false;
  public paymentTypes: Array<PaymentTypeDto>;
  public selectedPaymentTypeId: number;
  public showCCPayment = false;
  public showACHPayment = false;
  public arbCreate: ArbCreateDto;
  public arbStartDate: Date;
  public gatewayArbId: string;
  public today = new Date();
  public arb: ArbReadDto;
  public relatedProfiles: ProfileRelationshipManagementDto[];
  public allActiveUsers: Array<UserRead>;
  public profileAddressSelectItems = new Array<SelectItem>();
  public profileAddresses = new Array<ProfileAddress>();
  public defaultBillingAddress: ProfileAddress;
  public profileNames: ProfileMin;
  public showOverrides = false;
  public isPreczn = false;
  // Direct-gateway surface: the selected payment type already names the paying CCGatewayID, so
  // this is resolved from that id and never from revenue-item resolution.
  public resolvedGateway: TokenizeGatewayDto | null = null;
  // ATLAS-5709: anchor edits on a running Preczn ARB are confirmed or blocked
  public showAnchorChangeWarning = false;
  public showAnchorPastBlocked = false;
  private _committedAnchor: { month: number; day: number; year: number } | null = null;

  // Payment types an ARB can be filed against for the selected revenue item: its resolved gateways
  // that are not ARB-off Preczn gateways. Empty means the item cannot take an ARB at all.
  public arbPaymentTypeIds = new Set<number>();
  // Drops a stale gateway resolution when the revenue item has changed since the call started. Debouncing the
  // request is the usual way to avoid this; the item dropdown is not debounced, so the response is checked instead.
  private _arbResolveCallId = 0;
  private _arbResolvedItemNum = 0;

  public get arbPaymentTypes(): Array<PaymentTypeDto> {
    return (this.paymentTypes ?? []).filter((x) => this.arbPaymentTypeIds.has(x.PaymentTypeId));
  }

  public get arbUnavailable(): boolean {
    return this.arbPaymentTypeIds.size === 0 || this.arbPaymentTypes.length === 0;
  }

  public get isLegacyArb(): boolean {
    return !!this.value?.IsARB && !this.isPreczn;
  }

  // An ARB subscription exists at the gateway and its anchor fields are still editable (Preczn).
  public get isRunningEditableArb(): boolean {
    return !!this.gatewayArbId && !this.isLegacyArb;
  }

  public constructor(
    private _arcService: ArcService,
    private _billingService: BillingService,
    private _profileService: ProfileService,
    private _revenueItemService: RevenueItemService,
    private _translationService: TranslationService,
    private _utilService: UtilService,
    private _settingService: SettingService,
    private _paymentTypeService: PaymentTypeService,
    private _arbService: ArbService,
    private _notificationService: NotificationService,
    private _ccGatewayService: CreditCardGatewayService,
    private _userService: UserService,
    private _precznService: PrecznService,
    private _gatewayResolutionService: GatewayResolutionService
  ) {
    super({} as Billing, false);
    this.months = [];
    this.billingCycles = [];
    this.anchorDays = [];
    this.revenueItems = [];
  }

  // ngModel writes the billing after ngOnInit, so the item to resolve is only known here. Re-resolve
  // only when the written billing names a different item than the last resolution.
  writeValue(value: Billing) {
    super.writeValue(value);
    if ((+value?.ItemNum || 0) !== this._arbResolvedItemNum) {
      this.refreshArbPaymentTypes();
    }
  }

  ngOnInit() {
    this._arcService.searchArcs({ PageNumber: 0, PageSize: 0, Inactive: false } as ArcSearch).then((data) => {
      this.arcFormulas = data.Result.map((x) => ({ ArcID: x.ArcID, Descr: x.Descr, ItemNum: x.ItemNum } as ArcFormula));
      this.itemHasArc = this.arcFormulas.some((x) => x.ItemNum == this.value.ItemNum);
    });

    this.months = this._translationService.getMonths();
    this.anchorDays = Array(31)
      .fill(0, 0, 31)
      .map((x, i) => i + 1);

    this._billingService.getBillingCycles().then((data) => {
      this.billingCycles = data;
    });

    this._revenueItemService.getRevenueItems().then((data) => {
      this.revenueItems = data;
    });

    this._settingService.arbEnabled().then((data: boolean) => {
      this.arbIsEnabled = data;
      this.updateArbStart();
    });

    this._paymentTypeService.getPaymentTypes().then((data: Array<PaymentTypeDto>) => {
      // ValidAch, not AchType: an ACH payment type whose gateway cannot take ACH (Preczn with
      // eCheck off, or a type with no gateway at all) must not be offered here. Offering it and
      // then rendering no input - or rendering one the gateway will reject - is the worse UX, so
      // the dropdown filter and the showACHPayment gate below move together.
      this.paymentTypes = data.filter((x) => x.CreditCardType || x.ValidAch /* || x.CheckType*/);
    });
    this.arbCreate = initializedArbCreateDto();
    this.refreshArbPaymentTypes();
  }

  ngAfterViewInit() {
    // Set pristine after Angular has initially set the model
    setTimeout(() => {
      this.showBaseField = this._utilService.isNotNullAndGreaterThanZero(this.value.BasePrice);
      this.showFlatFeeField = this._utilService.isNotNullAndGreaterThanZero(this.value.FlatFee);
      this.setPristine();

      if (this.value.ARBID) {
        this._arbService.getArbByBillingId(this.value.BillingId).then((data: ArbReadDto) => {
          this.arb = data;

          this.arbSelected = true;
          this.gatewayArbId = data.SubscriptionKey;
          this.commitAnchor();

          if (data.PaymentTypeID) {
            Promise.all([
              this._paymentTypeService.getPaymentTypes(),
              this._ccGatewayService.getCreditCardGateways(),
            ]).then(([paymentTypes, gateways]) => {
              const pt = paymentTypes.find((p) => p.PaymentTypeId === data.PaymentTypeID);
              if (pt && pt.CcGatewayId) {
                const gw = gateways.find((g) => g.CCGatewayID === pt.CcGatewayId);
                const isPrecznGateway = gw ? gw.GatewayTypeID === 7 || gw.GatewayTypeID === 8 : false;
                const isAuthNet = +data.SubscriptionKey >= 1000000;
                this.isPreczn = isPrecznGateway && !isAuthNet;
              }
            });
          }
        });
      }

      this._profileService
        .getProfileRelationsForManagement({ ProfileId: this.value.ProfileId, PageNumber: 0, PageSize: 0 } as ProfileRelationSearch)
        .then((data) => {
          this._profileService.getProfile(this.value.ProfileId).then((profile) => {
            this.relatedProfiles = [
              { ReportName: profile?.ReportName, RelProfileID: profile?.ProfileId } as ProfileRelationshipManagementDto,
              ...data.Result,
            ];
          });
        });

      this._profileService.getAllProfileAddressed(this.value.ProfileId).then((result) => {
        this.profileAddresses = result;
        this.defaultBillingAddress =
          this.profileAddresses.find((x) => x.AddressType === 'Billing') || this.profileAddresses.find((x) => x.AddressType === 'Default');
        this.profileAddressSelectItems = result.map<SelectItem>((x) => {
          return { label: x.AddressType, value: x.ID };
        });
      });

      this._profileService.getProfileMin(this.value.ProfileId).then((data) => {
        this.profileNames = data;
      });

      this._userService.getActiveUsers().then((data) => {
        this.allActiveUsers = data.sort((x, y) => {
          if (x.UserName.toLowerCase() > y.UserName.toLowerCase()) {
            return 1;
          }
          if (x.UserName.toLowerCase() < y.UserName.toLowerCase()) {
            return -1;
          }
          return 0;
        });
      });
    }, 500);
  }

  revenueItemChanged(itemNum: any) {
    this.itemHasArc = this.arcFormulas.some((x) => x.ItemNum == itemNum);
    if (!this.arcFormulas.some((x) => x.ItemNum == itemNum && x.ArcID == this.arcId)) {
      this.arcId = 0;
    }
    const item = this.revenueItems.find((x) => x.ItemNum === parseInt(itemNum, 10));
    this.value.UnitPrice = item.UnitPrice;
    this.value.RevenueItem = item.ItemName;
    this.value.Taxable = item.Taxable;

    this.updateYearlyTotal();
    this.refreshArbPaymentTypes();
  }

  // Own resolveForRevenueItems result with a call counter: the service is a root singleton with a
  // shared subject, so a stale or foreign emission must never decide this row's eligibility.
  // Fail closed: no item, no gateway, or a failed resolution all leave the ARB unavailable.
  private refreshArbPaymentTypes(): void {
    const itemNum = +this.value?.ItemNum;
    const callId = ++this._arbResolveCallId;
    this._arbResolvedItemNum = itemNum > 0 ? itemNum : 0;
    if (!(itemNum > 0)) {
      this.applyArbPaymentTypes(new Set<number>());
      return;
    }
    this._gatewayResolutionService.resolveForRevenueItems([itemNum]).then((resolution) => {
      if (callId !== this._arbResolveCallId) {
        return;
      }
      const ids = new Set<number>();
      (resolution?.Gateways ?? [])
        .filter((g) => !!g && (g.ItemNums ?? []).includes(itemNum) && g.ArbCapable !== false)
        .forEach((g) => (g.PaymentTypes ?? []).forEach((pt) => ids.add(pt.PaymentTypeId)));
      this.applyArbPaymentTypes(ids);
    });
  }

  private applyArbPaymentTypes(ids: Set<number>): void {
    this.arbPaymentTypeIds = ids;
    if (this.gatewayArbId || this.value?.ARBID) {
      return;
    }
    if (this.arbUnavailable) {
      this.arbSelected = false;
    }
    if (this.selectedPaymentTypeId && !ids.has(+this.selectedPaymentTypeId)) {
      this.selectedPaymentTypeId = 0;
      this.selectedPaymentTypeChanged(0);
    }
  }

  arcChanged(arcId: number) {
    if (arcId > 0) {
      this._profileService.getCalculateProfileArc(this.value.ProfileId, arcId).then((amount) => {
        this.arcCalculation = amount;
      });
    }
  }

  async save(profileId: number): Promise<void> {
    // these fields might be left blank, so update them to 0 if so
    this.value.FlatFee = this.value.FlatFee ? this.value.FlatFee : 0;
    this.value.BasePrice = this.value.BasePrice ? this.value.BasePrice : 0;
    this.value.SoldBy = this.value.SoldBy ? this.value.SoldBy : 0;

    if (this.useArc && this.arcId > 0) {
      this.value.UnitPrice = this.arcCalculation;
      this.value.Quantity = 1;
      this.value.FlatFee = 0;
      this.value.BasePrice = 0;
    }

    if (this.value.StartingYear > 2100 || this.value.StartingYear < 1900) {
      this._notificationService.error(this._translationService.getInstant('profiles.edit.billing.invalid-starting-year'));
      return Promise.reject();
    }

    if (this.value.BillingId) {
      if (this.isDirty()) {
        this.value.AmountPerCycle = this.value.UnitPrice * this.value.Quantity + this.value.FlatFee + this.value.BasePrice;
        switch (this.value.BillingCycle.toLowerCase()) {
          case 'every 12 months':
            this.value.YearlyAmount = this.value.AmountPerCycle;
            break;
          case 'every 6 months':
            this.value.YearlyAmount = this.value.AmountPerCycle * 2;
            break;
          case 'every 3 months':
            this.value.YearlyAmount = this.value.AmountPerCycle * 4;
            break;
          case 'every month':
            this.value.YearlyAmount = this.value.AmountPerCycle * 12;
        }
        if (this.value.Taxable) {
          this.value.TaxAmount = await this._revenueItemService.getItemTaxAmount(this.value.ItemNum, this.value.AmountPerCycle);
        }
        return this._billingService
          .updateBilling(this.value)
          .then(() => {
            return this.processARBChanges();
          })
          .catch((error) => {
            this._notificationService.httpError(error);
          });
      } else {
        return this.processARBChanges();
      }
    } else {
      this.value.ProfileId = profileId;
      this.value.AmountPerCycle = this.value.UnitPrice * this.value.Quantity + this.value.FlatFee + this.value.BasePrice;
      switch (this.value.BillingCycle.toLowerCase()) {
        case 'every 12 months':
          this.value.YearlyAmount = this.value.AmountPerCycle;
          break;
        case 'every 6 months':
          this.value.YearlyAmount = this.value.AmountPerCycle * 2;
          break;
        case 'every 3 months':
          this.value.YearlyAmount = this.value.AmountPerCycle * 4;
          break;
        case 'every month':
          this.value.YearlyAmount = this.value.AmountPerCycle * 12;
      }
      if (this.value.Taxable) {
        this.value.TaxAmount = await this._revenueItemService.getItemTaxAmount(this.value.ItemNum, this.value.AmountPerCycle);
      }
      return this._billingService
        .createBilling(this.value)
        .then((data) => {
          this.value.BillingId = data;
          return this.processARBChanges();
        })
        .catch((error) => {
          this._notificationService.httpError(error);
        });
    }
  }

  // A payment is card or ACH - there is no in between, and no surface needs both widgets mounted
  // at once. The two branches are therefore exclusive and card-first, which is the instrument the
  // server already files for a dual-flagged type (ArbManager.CreateLegacyArbSubscription decides
  // it as `if (CreditCardType) ... else if (ACHType)`). Two independently settable checkboxes in
  // the payment-type admin dialog make a dual-flagged type configurable, so this is enforcement,
  // not an assertion: with sequential `if`s such a type mounted both widgets and offered staff an
  // ACH input whose data the server discards.
  selectedPaymentTypeChanged(paymentTypeId: number) {
    this.showCCPayment = false;
    this.showACHPayment = false;
    this.resolvedGateway = null;
    const paymentType = (this.paymentTypes ?? []).find((x) => x.PaymentTypeId === +paymentTypeId);
    if (paymentType?.CreditCardType) {
      this.setupArbDto();
      this._ccGatewayService.getCreditCardGateways().then((data: Array<CreditCardGateway>) => {
        const selectedGateway = data.find((x) => x.CCGatewayID === paymentType.CcGatewayId);

        if (selectedGateway && selectedGateway.GatewayTypeID === 6) {
          this.ccPaymentComponent.useCardConnect = true;
        } else {
          this.ccPaymentComponent.useCardConnect = false;
        }
      });

      this.updateResolvedGateway(paymentType.CcGatewayId);

      this.showCCPayment = true;
    } else if (paymentType?.ValidAch) {
      // The ACH widget needs its gateway for exactly the same reason the card widget does: without
      // it resolvedGateway stays null, the widget never leaves raw-field mode, and the subscribe
      // posts no Preczn token. The paying CCGatewayID is already in hand on the payment type.
      this.updateResolvedGateway(paymentType.CcGatewayId);

      this.showACHPayment = true;
    }
  }

  private updateResolvedGateway(ccGatewayId: number): void {
    this._precznService.resolveTokenizeGateway(ccGatewayId).then((gateway) => {
      this.resolvedGateway = gateway;
    });
  }

  updateArbStart() {
    this.arbStartDate = new Date(this.value.AnchorMonth + '/' + this.value.AnchorDay + '/' + this.value.StartingYear);
  }

  // Runs when an anchor month/day/year field changes (select change, or year input blur/enter).
  anchorChanged() {
    this.updateArbStart();

    if (!this.isRunningEditableArb || !this._committedAnchor) {
      return;
    }
    if (!(+this.value.AnchorMonth > 0) || !(+this.value.AnchorDay > 0) || !(+this.value.StartingYear > 0)) {
      return;
    }
    if (this.anchorMatchesCommitted()) {
      return;
    }

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    if (this.arbStartDate < startOfToday) {
      this.showAnchorPastBlocked = true;
    } else {
      this.showAnchorChangeWarning = true;
    }
  }

  confirmAnchorChange() {
    this.showAnchorChangeWarning = false;
    this.commitAnchor();
  }

  cancelAnchorChange() {
    this.showAnchorChangeWarning = false;
    this.revertAnchor();
  }

  acknowledgeAnchorPastBlocked() {
    this.showAnchorPastBlocked = false;
    this.revertAnchor();
  }

  private commitAnchor() {
    this._committedAnchor = { month: +this.value.AnchorMonth, day: +this.value.AnchorDay, year: +this.value.StartingYear };
  }

  private revertAnchor() {
    if (!this._committedAnchor) {
      return;
    }
    this.value.AnchorMonth = this._committedAnchor.month;
    this.value.AnchorDay = this._committedAnchor.day;
    this.value.StartingYear = this._committedAnchor.year;
    this.updateArbStart();
  }

  private anchorMatchesCommitted(): boolean {
    return (
      +this.value.AnchorMonth === this._committedAnchor.month &&
      +this.value.AnchorDay === this._committedAnchor.day &&
      +this.value.StartingYear === this._committedAnchor.year
    );
  }

  updateYearlyTotal() {
    let multiplier = 1;

    switch (this.value.BillingCycle) {
      case 'Every 6 Months':
        multiplier = 2;
        break;
      case 'Every 3 Months':
        multiplier = 4;
        break;
      case 'Every Month':
        multiplier = 12;
        break;
    }

    if (this.value.FlatFee) {
      this.value.YearlyAmount = this.value.FlatFee * multiplier;
    } else {
      this.value.YearlyAmount = (+(this.value.UnitPrice ?? 0) * +(this.value.Quantity ?? 1) + +(this.value.BasePrice ?? 0)) * multiplier;
    }
  }

  addressTypeChanged(addressTypeID) {
    let address = this.profileAddresses.find((x) => x.ID == addressTypeID);
    if (address != null) {
      this.value.BTAddress1 = address.Address1;
      this.value.BTAddress2 = address.Address2;
      this.value.BTCity = address.City;
      this.value.BTState = address.State;
      this.value.BTZip = address.Zip;
      this.value.BTCountry = address.Country;
    }
  }
  private setupArbDto() {
    this.arbCreate.BillingId = this.value.BillingId;
    this.arbCreate.ProfileID = this.value.ProfileId;
    this.arbCreate.PaymentTypeID = this.selectedPaymentTypeId;
    this.arbCreate.Inactive = false;
  }

  private mapCCPaymentInfo(ccInfo: CreditCardPaymentInfo) {
    // Card Info
    this.arbCreate.BillingId = this.value.BillingId;
    this.arbCreate.CardNum = ccInfo.CardNum;
    this.arbCreate.Last4Digits = ccInfo.CardNum.substr(ccInfo.CardNum.length - 4, 4);
    this.arbCreate.CCType = ccInfo.CardType;
    this.arbCreate.AmountPerCycle = this.value.AmountPerCycle;
    this.arbCreate.ExpireMonth = ccInfo.ValidThruMonth;
    this.arbCreate.ExpireYear = ccInfo.ValidThruYear;
    this.arbCreate.SecurityCode = ccInfo.SecurityCode;
    this.arbCreate.Descr = this.value.Descr;

    // CC Contact Info
    this.arbCreate.ContactInfo.Name = ccInfo.CreditCardContactInfo.NameOnCard;
    this.arbCreate.ContactInfo.Address1 = ccInfo.CreditCardContactInfo.Address1;
    this.arbCreate.ContactInfo.Address2 = ccInfo.CreditCardContactInfo.Address2;
    this.arbCreate.ContactInfo.City = ccInfo.CreditCardContactInfo.City;
    this.arbCreate.ContactInfo.State = ccInfo.CreditCardContactInfo.State;
    this.arbCreate.ContactInfo.Zip = ccInfo.CreditCardContactInfo.Zip;
    this.arbCreate.ContactInfo.ZipExt = ccInfo.CreditCardContactInfo.ZipExt;
    this.arbCreate.ContactInfo.County = ccInfo.CreditCardContactInfo.County;
    this.arbCreate.ContactInfo.Country = ccInfo.CreditCardContactInfo.Country;
    this.arbCreate.ContactInfo.Phone = ccInfo.CreditCardContactInfo.Phone;
    this.arbCreate.ContactInfo.Ext = ccInfo.CreditCardContactInfo.Ext;
    this.arbCreate.ContactInfo.Email = ccInfo.CreditCardContactInfo.Email;
    this.arbCreate.ContactInfo.IsCCAddress = true;
    this.arbCreate.ContactInfo.IsRefund = false;
    this.arbCreate.ContactInfo.FirstName = ccInfo.CreditCardContactInfo.FirstName;
    this.arbCreate.ContactInfo.LastName = ccInfo.CreditCardContactInfo.LastName;
    this.arbCreate.ContactInfo.OrgName = ccInfo.CreditCardContactInfo.OrgName;

    this.arbCreate.GatewayTokens = ccInfo.GatewayTokens;
  }

  updateACHInfo(achInfo: ArbCreateDto) {
    this.arbCreate = achInfo;
  }

  private arbCreateInFlight: Promise<void> | null = null;

  private async processARBChanges(): Promise<void> {
    if (this.arbSelected) {
      // Once-only: a billing that already has an ARB (loaded, or just created and not yet refreshed)
      // never reaches prepareForSubmit()/DTO mapping/createArb again. Checked synchronously.
      if (!this.value.ARBID && !this.gatewayArbId) {
        if (this.arbCreateInFlight) {
          return this.arbCreateInFlight;
        }
        const inFlight = this.createArbOnce();
        this.arbCreateInFlight = inFlight;
        const clear = () => {
          if (this.arbCreateInFlight === inFlight) {
            this.arbCreateInFlight = null;
          }
        };
        inFlight.then(clear, clear);
        return inFlight;
      } else {
        if (this.value.Inactive === true && this.arb?.Inactive !== true) {
          // cancel the ARB with the gateway
          this._arbService
            .cancelArbByBillingId(this.value.BillingId)
            .then((data: ArbCommandResultDto) => {
              if (data.Success) {
                const x = this._translationService.getInstant('profiles.edit.billing.arb-cancel-1');
                const y = this._translationService.getInstant('profiles.edit.billing.arb-cancel-2');
                const z = this._translationService.getInstant('profiles.edit.billing.arb-cancel-3');
                this._notificationService.success(x + this.value.BillingId.toString() + y + data.SubscriptionKey + z);
              } else {
                this.revertArbCancelFailure();
              }
            })
            .catch(() => {
              this.revertArbCancelFailure();
            })
            .finally(() => {
              return Promise.resolve().then(() => this.setPristine());
            });
        }
      }
    } else {
      return Promise.resolve().then(() => {
        this.setPristine();
      });
    }
  }

  private async createArbOnce(): Promise<void> {
    if (!this.arbPaymentTypeIds.has(+this.selectedPaymentTypeId)) {
      this._notificationService.error(this._translationService.getInstant('profiles.edit.billing.arb-unavailable'));
      return Promise.reject();
    }
    try {
      if (this.showCCPayment) {
        if (this.ccPaymentComponent.usePreczn) {
          await this.ccPaymentComponent.prepareForSubmit();
        }
        const ccInfo = this.ccPaymentComponent.getCreditCardPaymentInfo();
        this.mapCCPaymentInfo(ccInfo);
      } else if (this.showACHPayment) {
        // else-if, not a second `if`: card-first mutual exclusion, matching
        // selectedPaymentTypeChanged above. A dual-flagged payment type must file one
        // instrument, and the server picks card.
        //
        // ORDER IS LOAD-BEARING: prepareForSubmit() emits the widget's dto through
        // (achInfoChanges) -> updateACHInfo(), which assigns it to arbCreate WHOLESALE. So the
        // token must land on the widget's dto - and that dto must become arbCreate - before
        // setupArbDto() stamps BillingId/ProfileID/PaymentTypeID on it. Swapping these two lines
        // posts a dto with either no GatewayTokens or a zero BillingId.
        await this.achPaymentComponent.prepareForSubmit();
        //Calling setup here so we grab the BillingId, ProfileId, etc
        this.setupArbDto();
      }

      let result: ArbCreateResultDto;
      try {
        result = await this._arbService.createArb(this.arbCreate);
      } catch (error) {
        this.notifyArbCreateFailure(error?.message ?? '');
        return;
      }

      if (isArbId(result?.ExternalArbId)) {
        // the gateway created the subscription: never create again, even if it also returned a warning
        this.gatewayArbId = result.ExternalArbId;
        if (result.Error) {
          this._notificationService.error(this.arbBillingLabel() + ': ' + result.Error);
        }
        await this.refreshArbAfterCreate();
      } else {
        this.notifyArbCreateFailure(result?.Error || result?.ExternalArbId || '');
      }
    } finally {
      // a stale token must never be re-sent, whatever the outcome
      // (a copy, so the dto that was handed to createArb keeps what it actually sent)
      if (this.arbCreate) {
        this.arbCreate = { ...this.arbCreate, GatewayTokens: undefined };
      }
    }
  }

  private async refreshArbAfterCreate(): Promise<void> {
    try {
      const data = await this._arbService.getArbByBillingId(this.value.BillingId);
      if (data) {
        this.arb = data;
        this.value.ARBID = data.ARBID;
        this.value.IsARB = true;
      }
      this.setPristine();
    } catch (error) {
      this._notificationService.httpError(error);
    }
  }

  private notifyArbCreateFailure(detail: string): void {
    const retry = this._translationService.getInstant('profiles.edit.billing.arb-create-retry');
    // the billing identifier accompanies the retry guidance; without it the gateway message stands alone
    this._notificationService.error(retry ? `${this.arbBillingLabel()}: ${detail ? detail + ' ' : ''}${retry}` : detail);
  }

  private arbBillingLabel(): string {
    return `${this.value.BillingId}${this.value.Descr ? ' ' + this.value.Descr : ''}`;
  }

  // A Preczn token ARB set inactive stays inactive whatever the cancel call returns (ATLAS-5775).
  private revertArbCancelFailure(): void {
    if (this.isPreczn) {
      return;
    }
    this.value.Inactive = false;
    this._billingService.updateBilling(this.value).catch((error) => {
      this._notificationService.httpError(error);
    });
    this._notificationService.error(this._translationService.getInstant('profiles.edit.billing.arb-not-canceled'));
  }
}
